import React, { forwardRef } from 'react';
import { type VariantProps } from 'class-variance-authority';
import IcoMoon, { IconProps } from 'react-icomoon';
import { iconNames } from './Icon.constants';
import { cn } from '@/utils/cva';
import { setColor } from '@/utils/tailwind';
import { TColor } from '@/types/color';
import { iconVariants } from './Icon.styles';
import iconSet from './selection.json';

type TIconName = (typeof iconNames)[number];

interface IIconProps extends IconProps, VariantProps<typeof iconVariants> {
  icon: TIconName;
  size?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  color?: TColor | string;
}

export const iconSizeMapper: Record<NonNullable<IIconProps['size']>, number> = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 40,
  xl: 48,
  '2xl': 56,
};

const Icon = forwardRef<SVGElement, IIconProps>(
  ({ className, size, color, ...rest }, ref) => {
    const iconSize =
      typeof size === 'number' ? size : iconSizeMapper[size || 'sm'];
    return (
      <IcoMoon
        ref={ref}
        className={cn(iconVariants({ className }))}
        iconSet={iconSet}
        size={iconSize}
        {...rest}
        color={setColor(color)}
      />
    );
  }
);

Icon.displayName = 'Icon';

export { Icon, type IIconProps, type TIconName };
